'use client';

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Splide, SplideSlide, SplideProps } from '@splidejs/react-splide';
import type { Splide as SplideCore } from '@splidejs/splide';
import SplideArrows, { DefaultArrowProps } from './plugins/splideArrows';
import SplidePagination from './plugins/splidePagination';
import '@splidejs/splide/dist/css/splide.min.css';
import cn from './style.module.scss';

/* ---------------- TYPES ---------------- */

type Device = 'mobile' | 'tablet' | 'desktop';

type ResponsiveControls = {
  mobile?: {
    arrows?: boolean;
    pagination?: boolean;
  };
  tablet?: {
    arrows?: boolean;
    pagination?: boolean;
  };
  desktop?: {
    arrows?: boolean;
    pagination?: boolean;
  };
};

export type SplideSliderProps = {
  children?: React.ReactNode;
  slides?: React.ReactNode[];
  options?: SplideProps['options'];
  className?: string;
  blockClassName?: string;
  trackClassName?: string;
  listClassName?: string;
  ariaLabel?: string;
  slideClassName?: string;
  activeSlideClassName?: string;
  showArrows?: boolean;
  showPagination?: boolean;
  prevArrow?: React.ReactNode;
  nextArrow?: React.ReactNode;
  arrowsClassName?: string;
  prevClassName?: string;
  nextClassName?: string;
  defaultArrowProps?: DefaultArrowProps;
  paginationClassName?: string;
  dotClassName?: string;
  activeDotClassName?: string;
  responsive?: ResponsiveControls;
  pauseOnHover?: boolean;
  onSlideChange?: (index: number) => void;
} & React.HTMLAttributes<HTMLDivElement>;

/* ---------------- BREAKPOINTS ---------------- */

const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
};

/* ---------------- COMPONENT ---------------- */

const SplideSlider = forwardRef<any, SplideSliderProps>(
  (
    {
      children,
      slides,
      options = {},
      className = '',
      blockClassName = '',
      trackClassName = '',
      listClassName = '',
      slideClassName = '',
      activeSlideClassName = '',
      showArrows = false,
      showPagination = false,
      prevArrow,
      nextArrow,
      arrowsClassName = '',
      prevClassName = '',
      nextClassName = '',
      defaultArrowProps = {},
      paginationClassName = '',
      dotClassName = '',
      activeDotClassName = '',
      responsive,
      pauseOnHover = false,
      onSlideChange,
      ...rest
    },
    ref
  ) => {
    const slideItems = slides ?? React.Children.toArray(children);

    const splideRef = useRef<any>(null);
    const trackRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      splide: splideRef.current?.splide,
    }));

    const [ready, setReady] = useState(false);
    const [device, setDevice] = useState<Device>('desktop');
    const [activeIndex, setActiveIndex] = useState(0);

    /* -------- Sync track height with active slide (Splide only auto-sizes
       height for vertical sliders; horizontal sliders need this manually) -------- */

    useEffect(() => {
      const splide = splideRef.current?.splide;

      if (!splide) return undefined;

      let resizeObserver: ResizeObserver | undefined;

      const getActiveSlide = (): HTMLElement | undefined =>
        splide.Components?.Slides?.getAt(splide.index)?.slide;

      const syncHeight = () => {
        const track = trackRef.current;
        const activeSlide = getActiveSlide();

        if (!track || !activeSlide) return;

        track.style.height = `${activeSlide.scrollHeight}px`;

        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver.observe(activeSlide);
        }
      };

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(syncHeight);
      }

      // NOTE: splide.off() unbinds every listener for an event name (it has no
      // per-callback filter), which would also strip Splide's own internal
      // listeners. Don't call it here — Splide unbinds everything itself via
      // its own destroy lifecycle when the component unmounts.
      splide.on('mounted refresh resized moved updated', syncHeight);
      syncHeight();

      return () => {
        resizeObserver?.disconnect();
      };
    }, [ready]);

    /* -------- Device detection -------- */

    useEffect(() => {
      const detectDevice = () => {
        if (window.innerWidth <= BREAKPOINTS.mobile) {
          setDevice('mobile');
        } else if (window.innerWidth <= BREAKPOINTS.tablet) {
          setDevice('tablet');
        } else {
          setDevice('desktop');
        }
      };

      detectDevice();

      window.addEventListener('resize', detectDevice);

      return () => window.removeEventListener('resize', detectDevice);
    }, []);

    /* -------- Safe mount -------- */

    useEffect(() => {
      const id = setTimeout(() => {
        if (splideRef.current?.splide) {
          splideRef.current.splide.refresh();
          setActiveIndex(splideRef.current.splide.index);
        }

        setReady(true);
      }, 0);

      return () => clearTimeout(id);
    }, []);

    /* -------- Resolve controls per device -------- */

    const resolvedArrows = responsive?.[device]?.arrows ?? showArrows;

    const resolvedPagination =
      responsive?.[device]?.pagination ?? showPagination;

    /* ---------------- RENDER ---------------- */

    const handleMouseEnter = () => {
      if (!pauseOnHover) return;
      splideRef.current?.splide.Components.Autoplay.pause();
    };

    const handleMouseLeave = () => {
      if (!pauseOnHover) return;
      splideRef.current?.splide.Components.Autoplay.play();
    };

    return (
      <div
        {...rest}
        className={`${cn.splideContainer} ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Splide
          ref={splideRef}
          hasTrack={false}
          aria-label={rest.ariaLabel}
          onActive={(splide: SplideCore) => {
            setActiveIndex(splide.index);
            onSlideChange?.(splide.index);
          }}
          options={{
            ...options,
            arrows: false,
            pagination: false,
            autoplay: options.autoplay !== undefined ? options.autoplay : false,
            interval: options.interval || 3000,
            pauseOnHover,
          }}
          className={`${cn.splideBlock} ${blockClassName}`}
        >
          <div
            ref={trackRef}
            className={`splide__track ${trackClassName} ${cn.customTrack} `}
          >
            <ul className={`splide__list ${cn.customList} ${listClassName}`}>
              {slideItems.map((child, idx) => {
                const isActive = activeIndex === idx;

                return (
                  <SplideSlide
                    key={idx}
                    className={`${cn.splideSlide} ${slideClassName}`}
                  >
                    <div
                      className={`
                        ${cn.slideContent}
                        ${isActive ? activeSlideClassName : ''}
                        ${isActive ? cn.activeSlide : ''}
                      `}
                    >
                      {child}
                    </div>
                  </SplideSlide>
                );
              })}
            </ul>
          </div>
        </Splide>

        {ready && resolvedPagination && (
          <SplidePagination
            splideRef={splideRef}
            paginationClassName={paginationClassName}
            dotClassName={dotClassName}
            activeDotClassName={activeDotClassName}
          />
        )}

        {ready && resolvedArrows && (
          <SplideArrows
            splideRef={splideRef}
            arrowsClassName={arrowsClassName}
            prevArrow={prevArrow}
            nextArrow={nextArrow}
            prevClassName={prevClassName}
            nextClassName={nextClassName}
            defaultArrowProps={defaultArrowProps}
          />
        )}
      </div>
    );
  }
);

SplideSlider.displayName = 'SplideSlider';

export default SplideSlider;
